/**
 * Generic localStorage utility functions with error handling
 */

/**
 * Check if localStorage is available in the current environment
 */
export function isAvailable(): boolean {
  try {
    const testKey = "__localStorage_test__";
    localStorage.setItem(testKey, "test");
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

/**
 * Retrieve and parse a value from localStorage with fallback
 * @param key The localStorage key
 * @param defaultValue The default value to return if key doesn't exist or parsing fails
 * @returns The parsed value or defaultValue
 */
export function getItem<T>(key: string, defaultValue: T): T {
  if (!isAvailable()) {
    return defaultValue;
  }

  try {
    const item = localStorage.getItem(key);
    if (item === null) {
      return defaultValue;
    }
    return JSON.parse(item) as T;
  } catch {
    return defaultValue;
  }
}

/**
 * Stringify and save a value to localStorage
 * @param key The localStorage key
 * @param value The value to store
 */
export function setItem(key: string, value: unknown): void {
  if (!isAvailable()) {
    return;
  }

  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Silently fail if localStorage is full or unavailable
  }
}

/**
 * Remove an item from localStorage
 * @param key The localStorage key to remove
 */
export function removeItem(key: string): void {
  if (!isAvailable()) {
    return;
  }

  try {
    localStorage.removeItem(key);
  } catch {
    // Silently fail if localStorage is unavailable
  }
}
